import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PageHeader } from '@/components/PageHeader';
import { useAuthMe } from '@/hooks/useAuth';
import {
  useCimsoCapabilities,
  useCimsoCapabilitiesProbe,
  useCimsoSync,
  useOrgSettings,
  useUpdateOrgSettings,
} from '@/hooks/useDesk';
import { apiJson } from '@/lib/api';
import { brand } from '@/lib/brand';
import { publicEnv } from '@/lib/env';
import type { CimsoSyncResult, DeskSettings } from '@/types/desk';

type CimsoStatus = {
  configured: boolean;
  pullDisabled: boolean;
  allowListedReads: string[];
  writesEnabled: boolean;
  features?: string[];
};

type FormState = {
  company_name: string;
  legal_name: string;
  registration_no: string;
  vat_no: string;
  vat_rate: string;
  address: string;
  phone: string;
  email_from: string;
  logo_storage_path: string;
  quote_terms: string;
  invoice_terms: string;
  quote_number_prefix: string;
  invoice_number_prefix: string;
  next_quote_sequence: string;
  next_invoice_sequence: string;
  bank_name: string;
  bank_account_name: string;
  bank_account_number: string;
  bank_branch_code: string;
};

function fromSettings(s: DeskSettings): FormState {
  const bank = (s.banking_details || {}) as Record<string, string>;
  return {
    company_name: s.company_name || '',
    legal_name: s.legal_name || '',
    registration_no: s.registration_no || '',
    vat_no: s.vat_no || '',
    vat_rate: String(s.vat_rate ?? ''),
    address: s.address || '',
    phone: s.phone || '',
    email_from: s.email_from || '',
    logo_storage_path: s.logo_storage_path || '',
    quote_terms: s.quote_terms || '',
    invoice_terms: s.invoice_terms || '',
    quote_number_prefix: s.quote_number_prefix || 'Q',
    invoice_number_prefix: s.invoice_number_prefix || 'INV',
    next_quote_sequence: String(s.next_quote_sequence ?? 1),
    next_invoice_sequence: String(s.next_invoice_sequence ?? 1),
    bank_name: bank.bank_name || bank.name || '',
    bank_account_name: bank.account_name || '',
    bank_account_number: bank.account_number || '',
    bank_branch_code: bank.branch_code || '',
  };
}

export function SettingsPage() {
  const me = useAuthMe();
  const isAdmin = me.data?.user?.role === 'admin';
  const settings = useOrgSettings();
  const update = useUpdateOrgSettings();
  const [form, setForm] = useState<FormState | null>(null);

  useEffect(() => {
    if (settings.data) setForm(fromSettings(settings.data));
  }, [settings.data]);

  const status = useQuery({
    queryKey: ['cimso', 'status'],
    queryFn: () => apiJson<CimsoStatus>('/api/cimso/status'),
    retry: false,
  });

  const probe = useMutation({
    mutationFn: () =>
      apiJson<{ ok: boolean; membershipTypeCount: number | null }>(
        '/api/cimso/probe',
        { method: 'POST', body: '{}' },
      ),
  });
  const sync = useCimsoSync();
  const capabilities = useCimsoCapabilities();
  const probeCapabilities = useCimsoCapabilitiesProbe();
  const [lastSync, setLastSync] = useState<CimsoSyncResult | null>(null);

  async function onSave(e: FormEvent) {
    e.preventDefault();
    if (!form || !isAdmin) return;
    await update.mutateAsync({
      company_name: form.company_name.trim(),
      legal_name: form.legal_name.trim() || null,
      registration_no: form.registration_no.trim() || null,
      vat_no: form.vat_no.trim() || null,
      vat_rate: Number(form.vat_rate),
      address: form.address.trim() || null,
      phone: form.phone.trim() || null,
      email_from: form.email_from.trim() || null,
      logo_storage_path: form.logo_storage_path.trim() || null,
      quote_terms: form.quote_terms.trim() || null,
      invoice_terms: form.invoice_terms.trim() || null,
      quote_number_prefix: form.quote_number_prefix.trim() || 'Q',
      invoice_number_prefix: form.invoice_number_prefix.trim() || 'INV',
      next_quote_sequence: Number(form.next_quote_sequence) || 1,
      next_invoice_sequence: Number(form.next_invoice_sequence) || 1,
      banking_details: {
        bank_name: form.bank_name.trim() || undefined,
        account_name: form.bank_account_name.trim() || undefined,
        account_number: form.bank_account_number.trim() || undefined,
        branch_code: form.bank_branch_code.trim() || undefined,
      },
    });
  }

  function setField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  return (
    <div>
      <PageHeader
        title="Settings"
        description="Org branding, quote/invoice numbering, mailbox, Turnstile, Cimso status."
      />
      <div className="space-y-4">
        <section className="rounded-xl border border-[var(--border)] bg-white/80 p-5">
          <h2 className="font-display text-lg text-dome-forest">
            Organisation (domeec_settings)
          </h2>
          {settings.isLoading ? (
            <p className="mt-2 text-sm text-[var(--muted)]">Loading…</p>
          ) : null}
          {settings.isError ? (
            <p className="mt-2 text-sm text-[var(--muted)]">
              Settings unavailable until the settings migration is applied.
              Showing product identity only.
            </p>
          ) : null}

          {form ? (
            <form onSubmit={onSave} className="mt-4 grid gap-3 sm:grid-cols-2">
              {(
                [
                  ['company_name', 'Company name', true],
                  ['legal_name', 'Legal name', false],
                  ['registration_no', 'Registration no.', false],
                  ['vat_no', 'VAT no.', false],
                  ['vat_rate', 'VAT rate %', true],
                  ['phone', 'Phone', false],
                  ['email_from', 'Email from', false],
                  ['logo_storage_path', 'Logo storage path', false],
                  ['quote_number_prefix', 'Quote prefix', true],
                  ['invoice_number_prefix', 'Invoice prefix', true],
                  ['next_quote_sequence', 'Next quote #', true],
                  ['next_invoice_sequence', 'Next invoice #', true],
                ] as const
              ).map(([key, label, required]) => (
                <label key={key} className="text-sm">
                  <span className="mb-1 block font-medium">{label}</span>
                  <input
                    className="w-full rounded-lg border border-[var(--border)] px-3 py-2 disabled:bg-black/5"
                    required={required}
                    disabled={!isAdmin}
                    value={form[key]}
                    onChange={(e) => setField(key, e.target.value)}
                  />
                </label>
              ))}
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block font-medium">Address</span>
                <textarea
                  className="w-full rounded-lg border border-[var(--border)] px-3 py-2 disabled:bg-black/5"
                  rows={2}
                  disabled={!isAdmin}
                  value={form.address}
                  onChange={(e) => setField('address', e.target.value)}
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block font-medium">Quote terms</span>
                <textarea
                  className="w-full rounded-lg border border-[var(--border)] px-3 py-2 disabled:bg-black/5"
                  rows={3}
                  disabled={!isAdmin}
                  value={form.quote_terms}
                  onChange={(e) => setField('quote_terms', e.target.value)}
                />
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block font-medium">Invoice terms</span>
                <textarea
                  className="w-full rounded-lg border border-[var(--border)] px-3 py-2 disabled:bg-black/5"
                  rows={3}
                  disabled={!isAdmin}
                  value={form.invoice_terms}
                  onChange={(e) => setField('invoice_terms', e.target.value)}
                />
              </label>
              <fieldset className="sm:col-span-2 grid gap-3 rounded-lg border border-[var(--border)] p-3 sm:grid-cols-2">
                <legend className="px-1 text-sm font-medium">Banking</legend>
                {(
                  [
                    ['bank_name', 'Bank'],
                    ['bank_account_name', 'Account name'],
                    ['bank_account_number', 'Account number'],
                    ['bank_branch_code', 'Branch code'],
                  ] as const
                ).map(([key, label]) => (
                  <label key={key} className="text-sm">
                    <span className="mb-1 block font-medium">{label}</span>
                    <input
                      className="w-full rounded-lg border border-[var(--border)] px-3 py-2 disabled:bg-black/5"
                      disabled={!isAdmin}
                      value={form[key]}
                      onChange={(e) => setField(key, e.target.value)}
                    />
                  </label>
                ))}
              </fieldset>
              <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                {isAdmin ? (
                  <button
                    type="submit"
                    disabled={update.isPending}
                    className="rounded-lg bg-dome-forest px-4 py-2 text-sm font-medium text-dome-sand disabled:opacity-50"
                  >
                    {update.isPending ? 'Saving…' : 'Save settings'}
                  </button>
                ) : (
                  <p className="text-sm text-[var(--muted)]">
                    Signed in as staff — admins can edit org settings.
                  </p>
                )}
                {update.isSuccess ? (
                  <span className="text-sm text-dome-ok">Saved.</span>
                ) : null}
                {update.isError ? (
                  <span className="text-sm text-dome-danger">
                    {(update.error as Error).message}
                  </span>
                ) : null}
              </div>
            </form>
          ) : (
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[var(--muted)]">Organisation</dt>
                <dd>{brand.org}</dd>
              </div>
              <div>
                <dt className="text-[var(--muted)]">Product</dt>
                <dd>{brand.productFull}</dd>
              </div>
            </dl>
          )}
        </section>

        <section className="rounded-xl border border-[var(--border)] bg-white/80 p-5">
          <h2 className="font-display text-lg text-dome-forest">Runtime</h2>
          <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <dt className="text-[var(--muted)]">Pages project</dt>
              <dd className="font-mono text-xs">dome-events-conferencing</dd>
            </div>
            <div>
              <dt className="text-[var(--muted)]">Hostname</dt>
              <dd className="font-mono text-xs">
                {brand.hostnameTbd} (TBD)
              </dd>
            </div>
            <div>
              <dt className="text-[var(--muted)]">Data prefix</dt>
              <dd className="font-mono text-xs">
                domeec_* (tables) · dome_events_* (buckets)
              </dd>
            </div>
            <div>
              <dt className="text-[var(--muted)]">Supabase URL (public)</dt>
              <dd className="break-all font-mono text-xs">
                {publicEnv.supabaseUrl}
              </dd>
            </div>
          </dl>
        </section>

        <section className="rounded-xl border border-[var(--border)] bg-white/80 p-5">
          <h2 className="font-display text-lg text-dome-forest">Cimso</h2>
          {status.isLoading ? (
            <p className="mt-2 text-sm text-[var(--muted)]">Checking…</p>
          ) : status.isError ? (
            <p className="mt-2 text-sm text-[var(--muted)]">
              Status unavailable (sign-in + Functions required). Writes remain
              disabled.
            </p>
          ) : (
            <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[var(--muted)]">Configured</dt>
                <dd>{status.data?.configured ? 'yes' : 'no'}</dd>
              </div>
              <div>
                <dt className="text-[var(--muted)]">Pull disabled</dt>
                <dd>{status.data?.pullDisabled ? 'yes' : 'no'}</dd>
              </div>
              <div>
                <dt className="text-[var(--muted)]">Writes enabled</dt>
                <dd>{status.data?.writesEnabled ? 'yes' : 'no'}</dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[var(--muted)]">Allow-listed reads</dt>
                <dd className="font-mono text-xs">
                  {(status.data?.allowListedReads || []).join(', ') || '—'}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-[var(--muted)]">Client features</dt>
                <dd className="font-mono text-xs">
                  {(status.data?.features || []).join(', ') || '—'}
                </dd>
              </div>
            </dl>
          )}
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="rounded-lg bg-dome-forest px-3 py-2 text-sm font-medium text-dome-sand disabled:opacity-50"
              disabled={probe.isPending || status.data?.pullDisabled}
              onClick={() => probe.mutate()}
            >
              {probe.isPending ? 'Probing…' : 'Probe membership types (read)'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-dome-forest px-3 py-2 text-sm font-medium text-dome-forest disabled:opacity-50"
              disabled={
                sync.isPending ||
                status.data?.pullDisabled ||
                !status.data?.configured
              }
              onClick={() => {
                sync.mutate(
                  { dryRun: true, maxBookings: 50 },
                  { onSuccess: (r) => setLastSync(r) },
                );
              }}
            >
              {sync.isPending && sync.variables?.dryRun && !sync.variables?.debug
                ? 'Dry-run…'
                : 'Dry-run Sync from Cimso'}
            </button>
            {isAdmin ? (
              <button
                type="button"
                className="rounded-lg border border-dashed border-dome-forest px-3 py-2 text-sm font-medium text-dome-forest disabled:opacity-50"
                disabled={
                  sync.isPending ||
                  status.data?.pullDisabled ||
                  !status.data?.configured
                }
                onClick={() => {
                  sync.mutate(
                    { dryRun: true, debug: true, maxBookings: 25 },
                    { onSuccess: (r) => setLastSync(r) },
                  );
                }}
              >
                {sync.isPending && sync.variables?.debug
                  ? 'Debug dry-run…'
                  : 'Debug dry-run (admin)'}
              </button>
            ) : null}
            <button
              type="button"
              className="rounded-lg bg-dome-brass px-3 py-2 text-sm font-medium text-dome-ink disabled:opacity-50"
              disabled={
                sync.isPending ||
                status.data?.pullDisabled ||
                !status.data?.configured ||
                !isAdmin
              }
              onClick={() => {
                sync.mutate(
                  { dryRun: false, maxBookings: 100, mode: 'incremental' },
                  { onSuccess: (r) => setLastSync(r) },
                );
              }}
            >
              {sync.isPending &&
              !sync.variables?.dryRun &&
              sync.variables?.mode !== 'backfill'
                ? 'Syncing…'
                : 'Sync from Cimso'}
            </button>
            <button
              type="button"
              className="rounded-lg border border-dome-brass px-3 py-2 text-sm font-medium text-dome-brass disabled:opacity-50"
              disabled={
                sync.isPending ||
                status.data?.pullDisabled ||
                !status.data?.configured ||
                !isAdmin
              }
              onClick={() => {
                sync.mutate(
                  { dryRun: false, maxBookings: 100, mode: 'backfill' },
                  { onSuccess: (r) => setLastSync(r) },
                );
              }}
              title="Walk older bookings from the saved backfill cursor"
            >
              {sync.isPending && sync.variables?.mode === 'backfill'
                ? 'Backfilling…'
                : 'Backfill older'}
            </button>
            <button
              type="button"
              className="rounded-lg bg-dome-forest px-3 py-2 text-sm font-medium text-dome-sand disabled:opacity-50"
              disabled={
                sync.isPending ||
                status.data?.pullDisabled ||
                !status.data?.configured ||
                !isAdmin
              }
              onClick={() => {
                sync.mutate(
                  {
                    dryRun: false,
                    maxBookings: 150,
                    mode: 'full',
                    mirrorCatalog: true,
                  },
                  { onSuccess: (r) => setLastSync(r) },
                );
              }}
              title="Import all booking IDs across runs (resume via cursor)"
            >
              {sync.isPending && sync.variables?.mode === 'full'
                ? 'Full backfill…'
                : 'Full history import'}
            </button>
            {isAdmin ? (
              <button
                type="button"
                className="rounded-lg border border-dashed border-dome-forest px-3 py-2 text-sm font-medium text-dome-forest disabled:opacity-50"
                disabled={
                  probeCapabilities.isPending ||
                  status.data?.pullDisabled ||
                  !status.data?.configured
                }
                onClick={() => probeCapabilities.mutate()}
              >
                {probeCapabilities.isPending
                  ? 'Probing messages…'
                  : 'Probe Cimso capabilities'}
              </button>
            ) : null}
            {probe.isSuccess ? (
              <span className="text-sm text-dome-ok">
                OK — types: {probe.data.membershipTypeCount ?? 'n/a'}
              </span>
            ) : null}
            {probe.isError ? (
              <span className="text-sm text-dome-danger">
                {(probe.error as Error).message}
              </span>
            ) : null}
            {sync.isError ? (
              <span className="text-sm text-dome-danger">
                {(sync.error as Error).message}
              </span>
            ) : null}
          </div>
          {lastSync ? (
            <div className="mt-4 rounded-lg border border-[var(--border)] bg-dome-sand/40 p-3 text-sm">
              <p className="font-medium text-dome-forest">
                Last sync: {lastSync.status}
                {lastSync.dryRun ? ' (dry-run)' : ''}
              </p>
              <p className="mt-1 text-[var(--muted)]">
                Window {lastSync.window.fromIso} → {lastSync.window.toIso} ·
                ids {lastSync.stats.bookingIdsFetched ?? lastSync.stats.bookingsSeen} ·
                seen {lastSync.stats.bookingsSeen} · would create{' '}
                {lastSync.stats.eventsWouldCreate} / update{' '}
                {lastSync.stats.eventsWouldUpdate} · sheets{' '}
                {lastSync.stats.sheetsWouldUpsert}
                {(lastSync.stats.skippedNoStartsAt ?? 0) > 0
                  ? ` · skipped no-start ${lastSync.stats.skippedNoStartsAt}`
                  : ''}
                {(lastSync.stats.skippedNotBooking ?? 0) > 0
                  ? ` · skipped non-booking ${lastSync.stats.skippedNotBooking}`
                  : ''}
                {(lastSync.stats.skippedOutsideWindow ?? 0) > 0
                  ? ` · outside window ${lastSync.stats.skippedOutsideWindow}`
                  : ''}
                {lastSync.stats.stoppedPastWindow
                  ? ' · stopped past window'
                  : ''}
                {lastSync.stats.timedOut ? ' · timed out (partial)' : ''}
                {lastSync.stats.mode ? ` · ${lastSync.stats.mode}` : ''}
                {lastSync.stats.cursor
                  ? ` · cursor hw=${lastSync.stats.cursor.highWaterBookingId ?? '—'} bf=${lastSync.stats.cursor.backfillBookingId ?? '—'}`
                  : ''}
                {!lastSync.dryRun
                  ? ` · upserted events ${lastSync.stats.eventsUpserted}, sheets ${lastSync.stats.sheetsUpserted}`
                  : ''}
              </p>
              <p className="mt-1 font-mono text-xs text-[var(--muted)]">
                {(lastSync.stats.messagesCalled || []).join(', ') || 'no messages'}
                {lastSync.stats.bookingListVariantUsed
                  ? ` · list ${lastSync.stats.bookingListVariantUsed}`
                  : ''}
              </p>
              {(lastSync.stats.warnings || []).length > 0 ? (
                <ul className="mt-2 space-y-1 text-xs text-dome-brass">
                  {lastSync.stats.warnings!.map((w) => (
                    <li key={w}>{w}</li>
                  ))}
                </ul>
              ) : null}
              {lastSync.sample.length > 0 ? (
                <ul className="mt-2 space-y-1 text-xs">
                  {lastSync.sample.map((s) => (
                    <li key={s.cimsoBookingId}>
                      #{s.cimsoBookingId} {s.name} · {s.action} · items{' '}
                      {s.lineItemCount}
                    </li>
                  ))}
                </ul>
              ) : null}
              {(lastSync.debugSamples || []).length > 0 ? (
                <div className="mt-3 space-y-2">
                  <p className="text-xs font-medium text-dome-forest">
                    Debug samples (PII masked)
                  </p>
                  {lastSync.debugSamples!.map((d, i) => (
                    <details
                      key={`${d.message}-${i}`}
                      className="rounded border border-[var(--border)] bg-white/70 p-2 text-xs"
                    >
                      <summary className="cursor-pointer font-mono">
                        {d.message}
                        {d.errorCode != null ? ` · ec=${d.errorCode}` : ''}
                        {` · rows ${d.rowCount}`}
                        {d.warning ? ` · ${d.warning}` : ''}
                      </summary>
                      <p className="mt-1 text-[var(--muted)]">
                        fields: {d.fieldNames.join(', ') || '—'}
                      </p>
                      <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap break-all font-mono text-[10px]">
                        {JSON.stringify(d.rows, null, 2)}
                      </pre>
                    </details>
                  ))}
                </div>
              ) : null}
              {lastSync.error ? (
                <p className="mt-2 text-dome-danger">{lastSync.error}</p>
              ) : null}
            </div>
          ) : null}
          {(capabilities.data?.capabilities?.length ?? 0) > 0 ||
          probeCapabilities.isSuccess ? (
            <div className="mt-4 rounded-lg border border-[var(--border)] bg-dome-sand/40 p-3 text-sm">
              <p className="font-medium text-dome-forest">
                Cimso message capabilities (access-request list)
              </p>
              <ul className="mt-2 max-h-48 space-y-1 overflow-auto font-mono text-[10px]">
                {(capabilities.data?.capabilities || []).map((c) => (
                  <li key={c.message}>
                    {c.message} · {c.status}
                    {c.error_code != null ? ` · ec=${c.error_code}` : ''}
                    {c.detail ? ` · ${c.detail.slice(0, 80)}` : ''}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
          <p className="mt-3 text-xs text-[var(--muted)]">
            Pull only — Dome never writes to Cimso. Event names use client +
            first note/document title; Booking Reference is stored separately.
            Full history import resumes via cursor within the time budget. Cron:{' '}
            <code className="font-mono">POST /api/cimso/cron</code> with{' '}
            <code className="font-mono">X-Cimso-Sync-Secret</code> (defaults to{' '}
            <code className="font-mono">mode: full</code>).
          </p>
        </section>
      </div>
    </div>
  );
}
